import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';

import { Observable, catchError, filter, map, of, switchMap, take } from 'rxjs';

import { EntityService } from '@auth/service/entity.service';
import { AlertService } from '@common/service/alert.service';
import { NotificationService } from '@common/service/notification.service';
import { UserService } from '@common/service/user.service';
import { UserQuery } from '@common/store/user.store';
import { DLG_PARAM, ENTITY_PARAM, hasDialogLink } from '@common/util/dialog-link-params';
import { isEmpty } from '@common/util/util';

function buildCleanUrl(stateUrl: string, router: Router): UrlTree {
  const tree = router.parseUrl(stateUrl);
  delete tree.queryParams['entity'];
  return tree;
}

/** The URL without the dialog chain and without the account: what Cancel / no access / a failed switch land on. */
function buildDialogFreeUrl(stateUrl: string, router: Router): UrlTree {
  const tree = router.parseUrl(stateUrl);
  delete tree.queryParams[DLG_PARAM];
  delete tree.queryParams[ENTITY_PARAM];
  return tree;
}

/**
 * A link that carries `dlg`: same account opens as is; another account of the person asks first; no access
 * says so; signed out passes through untouched so the sign-in return path keeps `dlg` and `entity`.
 */
function dialogLinkEntity$(entityId: string, stateUrl: string): true | Observable<boolean | UrlTree> {
  const userQuery = inject(UserQuery);
  const userService = inject(UserService);
  // Same synchronous probe as legacy-module-route.guard: never subscribe to isUserAuthenticated$ here.
  if (isEmpty(userQuery.getValue()) && !userService.getLoginStatus()) {
    return true;
  }
  const entityService = inject(EntityService);
  const notificationService = inject(NotificationService);
  const alertService = inject(AlertService);
  const router = inject(Router);

  return userQuery.user$.pipe(
    filter((user) => !!user?.entity?.entity_id),
    take(1),
    switchMap((user): Observable<boolean | UrlTree> => {
      if (user.entity.entity_id === entityId) return of(true);

      const target = user.entity_list?.find((e) => e.entity_id === entityId);
      if (!target) {
        notificationService.notify({
          message: 'This link is for an account you don\x27t have access to.',
          duration: 5000,
        });
        return of(buildDialogFreeUrl(stateUrl, router));
      }

      return alertService
        .show('Switch account?', `This link is for ${target.company_name}. Switch?`, ['Cancel', 'Switch'])
        .afterClosed()
        .pipe(
          take(1),
          switchMap((choice): Observable<boolean | UrlTree> => {
            if (choice !== 'Switch') return of(buildDialogFreeUrl(stateUrl, router));
            return entityService.switchEntity(entityId).pipe(
              map((): boolean | UrlTree => {
                const companyName = userQuery.getValue().entity?.company_name ?? entityId;
                notificationService.notify({ message: `Switched to ${companyName}`, duration: 3000 });
                return true;
              }),
              catchError((err) => {
                console.warn('[EntitySwitchGuard] Entity switch failed:', err);
                return of(buildDialogFreeUrl(stateUrl, router));
              }),
            );
          }),
        );
    }),
  );
}

export const entitySwitchGuard: CanActivateFn = (route, state) => {
  const entityId = route.queryParamMap.get('entity');

  if (!entityId) {
    return true;
  }

  if (hasDialogLink(route.queryParams)) {
    return dialogLinkEntity$(entityId, state.url);
  }

  const userQuery = inject(UserQuery);
  const entityService = inject(EntityService);
  const notificationService = inject(NotificationService);
  const router = inject(Router);

  return userQuery.user$.pipe(
    filter((user) => !!user?.entity?.entity_id),
    take(1),
    switchMap((user) => {
      const cleanUrl = buildCleanUrl(state.url, router);

      console.debug('[EntitySwitchGuard] current entity_id:', user.entity.entity_id, '| requested:', entityId);

      if (user.entity.entity_id === entityId) {
        console.debug('[EntitySwitchGuard] already on requested entity — stripping param');
        return of<boolean | UrlTree>(cleanUrl);
      }

      console.debug('[EntitySwitchGuard] switching entity…');
      return entityService.switchEntity(entityId).pipe(
        map(() => {
          const companyName = userQuery.getValue().entity?.company_name ?? entityId;
          notificationService.notify({ message: `Switched to ${companyName}`, duration: 3000 });
          return cleanUrl as boolean | UrlTree;
        }),
        catchError((err) => {
          console.warn('[EntitySwitchGuard] Entity switch failed:', err);
          return of<boolean | UrlTree>(cleanUrl);
        }),
      );
    }),
  );
};
